import { Either, pipe, String } from 'effect';

interface IECriteria {
  inclusionCriteria: string;
  exclusionCriteria: string;
}

/**
 * The format is defined here: https://clinicaltrials.gov/policy/protocol-definitions#EligibilityCriteria
 *
 * It states that the headers should be `Inclusion Criteria` and `Exclusion Criteria`
 *
 * Unfortunately, in practice, the format is not always consistent.
 *
 * We have encountered the following formats:
 * - `Inclusion Criteria:`
 * - `Key inclusion criteria:`
 * - `Key Inclusion Criteria`
 *
 * This regex will match those three formats.
 *
 * Please note how the two capture groups are used:
 *
 * - They both use `[\s\S]` to match any character, including newlines (unlike `.`).
 *
 * - `([\s\S]+?)` captures the inclusion criteria.
 *
 * It is lazy -> it will match as few characters as possible.
 *
 * This is important or else it will try to match the entire string, up to the end.
 *
 * - `([\s\S]+)` captures the exclusion criteria.
 *
 * It is greedy -> it will match as many characters as possible.
 *
 * This is important or else it will stop before the end of the string.
 */
const criteriaRegex =
  /^(?:Key\s+)?Inclusion\s+Criteria:?\s*([\s\S]+?)\s*(?:Key\s+)?Exclusion\s+Criteria:?\s*([\s\S]+)$/im;

export const formatIECriteria = ({
  eligibilityCriteria,
  minimumAge,
  sex,
}: {
  eligibilityCriteria: string;
  minimumAge: string;
  sex: string;
}) =>
  pipe(
    splitInclusionExclusionCriteria(eligibilityCriteria),
    addMinimumAgeAndSexToInclusionCriteria({
      minimumAge,
      sex,
    }),
  );

const haveBothCriteriaBeenParsed = ({
  inclusionCriteria,
  exclusionCriteria,
}: IECriteria) =>
  String.isNonEmpty(inclusionCriteria) && String.isNonEmpty(exclusionCriteria);

const splitInclusionExclusionCriteria = (IECriteria: string) => {
  const match = IECriteria.match(criteriaRegex);

  const inclusionCriteria = match?.[1] ?? '';
  const exclusionCriteria = match?.[2] ?? '';

  return pipe(
    {
      inclusionCriteria,
      exclusionCriteria,
    },
    Either.liftPredicate(haveBothCriteriaBeenParsed, criteria => ({
      raw: IECriteria,
      ...criteria,
    })),
  );
};

const addMinimumAgeAndSexToInclusionCriteria =
  ({ minimumAge, sex }: { minimumAge: string; sex: string }) =>
  (
    IECriteria: Either.Either<
      IECriteria,
      {
        raw: string;
      } & IECriteria
    >,
  ) => {
    const mapCriteria = (criteria: IECriteria) => ({
      inclusionCriteria: criteria.inclusionCriteria.concat(
        formatMinimumAge(minimumAge),
        formatSex(sex),
      ),
      exclusionCriteria: criteria.exclusionCriteria,
    });

    return Either.mapBoth(IECriteria, {
      onRight: right => mapCriteria(right),
      onLeft: left => ({
        ...mapCriteria(left),
        raw: left.raw,
      }),
    });
  };

const formatMinimumAge = (age: string) =>
  age === '' ? '' : `\n* Should be at least ${age.toLowerCase()} of age`;

const formatSex = (sex: string) =>
  sex === 'ALL' || sex === '' ? '' : `\n* Should be a ${sex.toLowerCase()}`;
